import React, { useState } from 'react';
import { 
    Box, 
    Button, 
    Typography, 
    Paper, 
    Grid,
    FormHelperText,
    IconButton,
    CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';

/**
 * Component for document uploads section of venue onboarding form
 */
const DocumentUploadSection = ({ 
    title,
    description,
    isRequired,
    documentType,
    acceptedFileTypes,
    files,
    onFileAdd,
    onFileRemove,
    error,
    maxFiles = 10 // Default to unlimited files
}) => {
    const [isUploading, setIsUploading] = useState(false);

    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        if (selectedFiles.length > 0) {
            setIsUploading(true);
            
            // Check if adding these files would exceed the maxFiles limit
            const totalFiles = (files?.length || 0) + selectedFiles.length;
            const filesToAdd = maxFiles < Infinity 
                ? selectedFiles.slice(0, Math.max(0, maxFiles - (files?.length || 0))) 
                : selectedFiles;
            
            // Simulate upload delay
            setTimeout(() => {
                if (filesToAdd.length > 0) {
                    onFileAdd(documentType, filesToAdd);
                }
                setIsUploading(false);
            }, 1000);
        }
    };

    // Helper function to get icon based on file type
    const getFileIcon = (fileName) => {
        if (fileName.match(/\.(pdf)$/i)) {
            return <PictureAsPdfIcon />;
        } else if (fileName.match(/\.(jpg|jpeg|png|gif)$/i)) {
            return <ImageIcon />;
        } else {
            return <InsertDriveFileIcon />;
        }
    };

    // Convert the acceptedFileTypes array to a string for the file input
    const acceptTypesString = acceptedFileTypes?.join(',');
    
    // Determine if upload should be disabled based on max files
    const isUploadDisabled = isUploading || (maxFiles !== Infinity && (files?.length || 0) >= maxFiles);

    return (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
                {title} {isRequired && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {description}
            </Typography>
            
            <Box sx={{ mb: 2 }}>
                <input
                    accept={acceptTypesString}
                    style={{ display: 'none' }}
                    id={`upload-file-${documentType}`}
                    type="file"
                    multiple={maxFiles !== 1}
                    onChange={handleFileChange}
                    disabled={isUploadDisabled}
                />
                <label htmlFor={`upload-file-${documentType}`}>
                    <Button
                        variant="contained"
                        component="span"
                        startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                        disabled={isUploadDisabled}
                    >
                        {isUploading ? 'Uploading...' : 'Upload Files'}
                    </Button>
                </label>
                
                {error && (
                    <FormHelperText error sx={{ mt: 1 }}>
                        {error}
                    </FormHelperText>
                )}
                
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Accepted file types: {acceptedFileTypes?.join(', ')}
                    {maxFiles !== Infinity && ` (Maximum ${maxFiles} file${maxFiles !== 1 ? 's' : ''})`}
                </Typography>
            </Box>
            
            {files && files.length > 0 && (
                <Grid container spacing={2}>
                    {files.map((file, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper 
                                variant="outlined" 
                                sx={{ 
                                    p: 2, 
                                    display: 'flex', 
                                    alignItems: 'center',
                                    position: 'relative'
                                }}
                            >
                                {getFileIcon(file.name)}
                                <Box sx={{ ml: 1, overflow: 'hidden', flexGrow: 1 }}>
                                    <Typography 
                                        variant="body2" 
                                        noWrap 
                                        title={file.name}
                                    >
                                        {file.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {(file.size / 1024).toFixed(2)} KB
                                    </Typography>
                                </Box>
                                <IconButton 
                                    size="small" 
                                    color="error" 
                                    onClick={() => onFileRemove(documentType, index)}
                                    sx={{ ml: 1 }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Paper>
    );
};

export default DocumentUploadSection; 